import { createContext, useCallback, useContext, useMemo, useState } from "react";

export const MODAL_TYPES = {
    PACKAGE: 'package',
    CREATE_FOLDER: 'createFolder',
    RENAME_FOLDER: 'renameFolder',
    DELETE_FOLDER: 'deleteFolder',
    MOVE_FILE_TO_FOLDER: 'moveFileToFolder',
};

const ModalContext = createContext({
    activeModal: null,
    modalPayload: null,
    foldersVersion: 0,
    openModal: () => { },
    closeModal: () => { },
    isModalOpen: () => false,
    refreshFolders: () => { },
});

export const ModalProvider = ({ children }) => {
    const [activeModal, setActiveModal] = useState(null);
    const [modalPayload, setModalPayload] = useState(null);
    const [foldersVersion, setFoldersVersion] = useState(0);

    const openModal = useCallback((modalName, payload = null) => {
        setActiveModal(modalName);
        setModalPayload(payload);
    }, []);

    const closeModal = useCallback(() => {
        setActiveModal(null);
        setModalPayload(null);
    }, []);

    const isModalOpen = useCallback((modalName) => activeModal === modalName, [activeModal]);
    const refreshFolders = useCallback(() => {
        setFoldersVersion((currentValue) => currentValue + 1);
    }, []);

    const value = useMemo(() => ({
        activeModal,
        modalPayload,
        foldersVersion,
        openModal,
        closeModal,
        isModalOpen,
        refreshFolders,
    }), [activeModal, closeModal, foldersVersion, isModalOpen, modalPayload, openModal, refreshFolders]);

    return (
        <ModalContext.Provider value={value}>
            {children}
        </ModalContext.Provider>
    );
};

export const useModal = () => useContext(ModalContext);

export const usePackageModal = () => {
    const { modalPayload, openModal, closeModal, isModalOpen } = useModal();

    return {
        isOpen: isModalOpen(MODAL_TYPES.PACKAGE),
        payload: isModalOpen(MODAL_TYPES.PACKAGE) ? modalPayload : null,
        openModal: (payload = null) => openModal(MODAL_TYPES.PACKAGE, payload),
        closeModal,
    };
};

export const useMoveFileToFolderModal = () => {
    const { modalPayload, openModal, closeModal, isModalOpen } = useModal();

    return {
        isOpen: isModalOpen(MODAL_TYPES.MOVE_FILE_TO_FOLDER),
        payload: isModalOpen(MODAL_TYPES.MOVE_FILE_TO_FOLDER) ? modalPayload : null,
        openModal: (payload = null) => openModal(MODAL_TYPES.MOVE_FILE_TO_FOLDER, payload),
        closeModal,
    };
};

export const useCreateFolderModal = () => {
    const { modalPayload, openModal, closeModal, isModalOpen } = useModal();

    return {
        isOpen: isModalOpen(MODAL_TYPES.CREATE_FOLDER),
        payload: isModalOpen(MODAL_TYPES.CREATE_FOLDER) ? modalPayload : null,
        openModal: (payload = null) => openModal(MODAL_TYPES.CREATE_FOLDER, payload),
        closeModal,
    };
};

export const useFolderRefresh = () => {
    const { foldersVersion, refreshFolders } = useModal();

    return {
        foldersVersion,
        refreshFolders,
    };
};

export const useRenameFolderModal = () => {
    const { modalPayload, openModal, closeModal, isModalOpen } = useModal();

    return {
        isOpen: isModalOpen(MODAL_TYPES.RENAME_FOLDER),
        payload: isModalOpen(MODAL_TYPES.RENAME_FOLDER) ? modalPayload : null,
        openModal: (payload = null) => openModal(MODAL_TYPES.RENAME_FOLDER, payload),
        closeModal,
    };
};

export const useDeleteFolderModal = () => {
    const { modalPayload, openModal, closeModal, isModalOpen } = useModal();

    return {
        isOpen: isModalOpen(MODAL_TYPES.DELETE_FOLDER),
        payload: isModalOpen(MODAL_TYPES.DELETE_FOLDER) ? modalPayload : null,
        openModal: (payload = null) => openModal(MODAL_TYPES.DELETE_FOLDER, payload),
        closeModal,
    };
};
